import { useCallback, useEffect, useState } from 'react';
import type { AppRole } from './scope';

const STORAGE_KEY = 'wfp-v2:onboarding:v1';

export interface OnboardingState {
  completedAt: string | null;
  lastSeenRole: AppRole | null;
  skippedSteps: string[];
}

const DEFAULT_STATE: OnboardingState = {
  completedAt: null,
  lastSeenRole: null,
  skippedSteps: [],
};

export function readOnboardingState(): OnboardingState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return {
      completedAt: parsed.completedAt ?? null,
      lastSeenRole: (parsed.lastSeenRole as AppRole | null) ?? null,
      skippedSteps: Array.isArray(parsed.skippedSteps) ? parsed.skippedSteps : [],
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export function writeOnboardingState(state: OnboardingState): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function resetOnboardingState(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

const EVENT_NAME = 'wfp-v2:onboarding-changed';

export function useOnboardingState() {
  const [state, setState] = useState<OnboardingState>(() => readOnboardingState());

  useEffect(() => {
    function refresh() {
      setState(readOnboardingState());
    }
    window.addEventListener(EVENT_NAME, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const update = useCallback((patch: Partial<OnboardingState>) => {
    const next = { ...readOnboardingState(), ...patch };
    writeOnboardingState(next);
    setState(next);
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  const complete = useCallback(
    (role: AppRole) => {
      update({ completedAt: new Date().toISOString(), lastSeenRole: role });
    },
    [update],
  );

  const reset = useCallback(() => {
    resetOnboardingState();
    setState(DEFAULT_STATE);
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  return { state, update, complete, reset };
}
